import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import communicationsApi, { 
  Communication, 
  CommunicationStats, 
  EnhancedStats,
  DailySummary,
  CommunicationFilters,
  PaginationParams 
} from '../../services/api/communications'

// State interfaces
interface CommunicationsState {
  communications: Communication[]
  selectedCommunication: Communication | null
  stats: CommunicationStats | null
  enhancedStats: EnhancedStats | null
  dailySummary: DailySummary | null
  recentCommunications: Communication[]
  loading: boolean
  statsLoading: boolean
  error: string | null
  filters: CommunicationFilters
  pagination: {
    skip: number
    limit: number
    total: number
  }
  searchQuery: string
  successMessage: string
}

// Initial state
const initialState: CommunicationsState = {
  communications: [],
  selectedCommunication: null,
  stats: null,
  enhancedStats: null,
  dailySummary: null,
  recentCommunications: [],
  loading: false,
  statsLoading: false,
  error: null,
  filters: {},
  pagination: {
    skip: 0,
    limit: 20,
    total: 0,
  },
  searchQuery: '',
  successMessage: '',
}

// Async thunks
export const fetchCommunications = createAsyncThunk(
  'communications/fetchAll',
  async ({ filters, pagination }: { filters?: CommunicationFilters; pagination?: PaginationParams }) => {
    const response = await communicationsApi.listCommunications(filters, pagination)
    return response.data
  }
)

export const searchCommunications = createAsyncThunk(
  'communications/search',
  async ({ query, filters, pagination }: { 
    query: string; 
    filters?: CommunicationFilters; 
    pagination?: PaginationParams 
  }) => {
    const response = await communicationsApi.searchCommunications(query, filters, pagination)
    return response.data
  }
)

export const fetchCommunicationById = createAsyncThunk(
  'communications/fetchById',
  async (id: string) => {
    const response = await communicationsApi.getCommunicationById(id)
    return response.data
  }
)

export const fetchCommunicationStats = createAsyncThunk(
  'communications/fetchStats',
  async (filters?: {
    type?: string
    organizationId?: number
    branchId?: number
    environment?: string
    dateFrom?: string
    dateTo?: string
  }) => {
    const response = await communicationsApi.getCommunicationStats(filters)
    return response.data
  }
)

export const fetchEnhancedStats = createAsyncThunk(
  'communications/fetchEnhancedStats',
  async (filters?: {
    type?: string
    organizationId?: number
    branchId?: number
    dateFrom?: string
    dateTo?: string
  }) => {
    const response = await communicationsApi.getEnhancedStats(filters)
    return response.data
  }
)

export const fetchRecentCommunications = createAsyncThunk(
  'communications/fetchRecent',
  async ({ limit = 100, filters }: { 
    limit?: number; 
    filters?: {
      type?: string
      status?: string
      organizationId?: number
      environment?: string
    }
  }) => {
    const response = await communicationsApi.getRecentCommunications(limit, filters)
    return response.data
  }
)

export const fetchDailySummary = createAsyncThunk(
  'communications/fetchDailySummary',
  async (date?: string) => {
    const response = await communicationsApi.getDailySummary(date)
    return response.data
  }
)

export const resendCommunication = createAsyncThunk(
  'communications/resend',
  async (id: string) => {
    const response = await communicationsApi.resendCommunication(id)
    return response.data
  }
)

export const exportCommunications = createAsyncThunk(
  'communications/export',
  async ({ format, filters }: { format: 'csv' | 'json'; filters?: CommunicationFilters }) => {
    const response = await communicationsApi.exportCommunications(format, filters)
    
    // Handle file download
    if (format === 'csv') {
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `communications_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      return { success: true, format }
    }
    
    return response.data
  }
)

// Slice
const communicationsSlice = createSlice({
  name: 'communications',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<CommunicationFilters>) => {
      state.filters = action.payload
      state.pagination.skip = 0 // Reset pagination when filters change
    },
    clearFilters: (state) => {
      state.filters = {}
      state.pagination.skip = 0
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setPagination: (state, action: PayloadAction<{ skip?: number; limit?: number }>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    clearSelectedCommunication: (state) => {
      state.selectedCommunication = null
    },
    clearError: (state) => {
      state.error = null
    },
    clearSuccessMessage: (state) => {
      state.successMessage = ''
    },
    setSuccessMessage: (state, action: PayloadAction<string>) => {
      state.successMessage = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch all communications
    builder
      .addCase(fetchCommunications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommunications.fulfilled, (state, action) => {
        state.loading = false
        
        // Handle both formats: { items: [], total: n } or direct array
        if (action.payload && typeof action.payload === 'object') {
          if ('items' in action.payload) {
            state.communications = action.payload.items || []
            state.pagination.total = action.payload.total || 0
          } else if (Array.isArray(action.payload)) {
            state.communications = action.payload
            state.pagination.total = action.payload.length
          } else {
            // Might be a direct response without items wrapper
            state.communications = []
            state.pagination.total = 0
            console.log('Unknown format, setting empty')
          }
        } else {
          state.communications = []
          state.pagination.total = 0
          console.log('No payload, setting empty')
        }
      })
      .addCase(fetchCommunications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch communications'
      })

    // Search communications
    builder
      .addCase(searchCommunications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(searchCommunications.fulfilled, (state, action) => {
        state.loading = false
        state.communications = action.payload.items || action.payload
        state.pagination.total = action.payload.total || state.communications.length
      })
      .addCase(searchCommunications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Search failed'
      })

    // Fetch by ID
    builder
      .addCase(fetchCommunicationById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCommunicationById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedCommunication = action.payload
      })
      .addCase(fetchCommunicationById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch communication'
      })

    // Fetch stats
    builder
      .addCase(fetchCommunicationStats.pending, (state) => {
        state.statsLoading = true
      })
      .addCase(fetchCommunicationStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.stats = action.payload
      })
      .addCase(fetchCommunicationStats.rejected, (state) => {
        state.statsLoading = false
      })

    // Fetch enhanced stats
    builder
      .addCase(fetchEnhancedStats.pending, (state) => {
        state.statsLoading = true
      })
      .addCase(fetchEnhancedStats.fulfilled, (state, action) => {
        state.statsLoading = false
        state.enhancedStats = action.payload
      })
      .addCase(fetchEnhancedStats.rejected, (state) => {
        state.statsLoading = false
      })

    // Fetch recent
    builder
      .addCase(fetchRecentCommunications.fulfilled, (state, action) => {
        state.recentCommunications = action.payload
      })

    // Fetch daily summary
    builder
      .addCase(fetchDailySummary.pending, (state) => {
        state.statsLoading = true
      })
      .addCase(fetchDailySummary.fulfilled, (state, action) => {
        state.statsLoading = false
        state.dailySummary = action.payload
      })
      .addCase(fetchDailySummary.rejected, (state) => {
        state.statsLoading = false
      })

    // Resend communication
    builder
      .addCase(resendCommunication.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resendCommunication.fulfilled, (state, action) => {
        state.loading = false
        state.successMessage = action.payload.message || 'Communication queued for resending'
        // Update the communication in the list if it exists
        const index = state.communications.findIndex(c => c._id === action.payload.data?._id)
        if (index !== -1 && action.payload.data) {
          state.communications[index] = action.payload.data
        }
      })
      .addCase(resendCommunication.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to resend communication'
      })

    // Export communications
    builder
      .addCase(exportCommunications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(exportCommunications.fulfilled, (state, action) => {
        state.loading = false
        if (action.payload.success) {
          state.successMessage = `Exported as ${action.payload.format.toUpperCase()}`
        }
      })
      .addCase(exportCommunications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Export failed'
      })
  },
})

// Export actions
export const {
  setFilters,
  clearFilters,
  setSearchQuery,
  setPagination,
  clearSelectedCommunication,
  clearError,
  clearSuccessMessage,
  setSuccessMessage,
} = communicationsSlice.actions

// Export reducer
export default communicationsSlice.reducer